import React from 'react';

const PrivacyContent: React.FC = () => {
  return (
    <div className="max-w-none text-gray-800 space-y-8 py-2">
      <p className="text-[15px] leading-relaxed text-gray-700">
        This privacy policy will explain how PRAIRI uses the personal data we collect from you when
        you use our website.
      </p>

      <section>
        <h3 className="text-lg font-extrabold text-gray-900 mb-4 tracking-tight">Topics</h3>
        <ul className="list-disc pl-5 space-y-2 text-[15px] text-gray-700">
          <li>What data do we collect?</li>
          <li>How do we collect your data?</li>
          <li>How will we use your data?</li>
          <li>How do we store your data?</li>
          <li>Marketing</li>
          <li>What are your data protection rights?</li>
          <li>What are cookies?</li>
          <li>How do we use cookies?</li>
          <li>What types of cookies do we use?</li>
          <li>How to manage your cookies</li>
          <li>Privacy policies of other websites</li>
          <li>Changes to our privacy policy</li>
          <li>How to contact us</li>
          <li>How to contact the appropriate authorities</li>
        </ul>
      </section>

      <section>
        <h3 className="text-lg font-extrabold text-gray-900 mb-2 tracking-tight">
          What data do we collect?
        </h3>
        <p className="text-[15px] leading-relaxed text-gray-700 mb-4">
          PRAIRI collects the following data:
        </p>
        <ul className="list-disc pl-5 space-y-2 text-[15px] text-gray-700">
          <li>Personal identification information (Name, email address, phone number, etc.)</li>
          <li>
            PRAIRI also collects information about your business, which you provide to us, so that
            we can match you to relevant individuals and/or companies.
          </li>
        </ul>
      </section>

      <section>
        <h3 className="text-lg font-extrabold text-gray-900 mb-2 tracking-tight">
          How do we collect your data?
        </h3>
        <p className="text-[15px] leading-relaxed text-gray-700">
          You directly provide PRAIRI with most of the data we collect. We collect data and process
          data when you:
        </p>
        <ul className="list-disc pl-5 space-y-2 text-[15px] text-gray-700 mt-4">
          <li>Register online and/or fill out a &lsquo;Data Profile&rsquo;.</li>
          <li>Voluntarily complete a customer survey or provide feedback.</li>
          <li>Use or view our website via your browser&rsquo;s cookies.</li>
        </ul>
      </section>

      <section>
        <h3 className="text-lg font-extrabold text-gray-900 mb-2 tracking-tight">
          How will we use your data?
        </h3>
        <p className="text-[15px] leading-relaxed text-gray-700">
          PRAIRI collects your data so that we can:
        </p>
        <ul className="list-disc pl-5 space-y-2 text-[15px] text-gray-700 mt-4">
          <li>Match you to relevant individuals and/or companies.</li>
          <li>Email/notify you when there is a match or other related or relevant information.</li>
          <li>
            Email/notify you with special offers on other products and services we think you might
            like.
          </li>
        </ul>
        <p className="text-[15px] leading-relaxed text-gray-700 mt-4">
          If you opted in as part of your Data Profile and agreed, PRAIRI will share your data with
          our partner companies so that they may offer you their products and services.
        </p>
        <ul className="list-disc pl-5 mt-2 text-[15px] text-gray-700">
          <li>Companies offering valuable services to startups</li>
        </ul>
      </section>

      <section>
        <h3 className="text-lg font-extrabold text-gray-900 mb-2 tracking-tight">
          How do we store your data?
        </h3>
        <p className="text-[15px] leading-relaxed text-gray-700 mb-4">
          PRAIRI securely stores your data on European servers guarded with Helmet and Growler
          security protocols.
        </p>
        <p className="text-[15px] leading-relaxed text-gray-700">
          PRAIRI will keep your data for as long as you keep your account. At any time, you may go
          to your profile settings and choose to delete your profile and data, as you have the right
          &lsquo;to be forgotten&rsquo;. Once this time period has expired, we will make reasonable
          effort to immediately delete your data but at least within 10-14 days.
        </p>
      </section>

      <section>
        <h3 className="text-lg font-extrabold text-gray-900 mb-2 tracking-tight">Marketing</h3>
        <p className="text-[15px] leading-relaxed text-gray-700 mb-4">
          PRAIRI would like to send you information about products and services of ours that we
          think you might like, as well as those of our partner companies.
        </p>
        <p className="text-[15px] leading-relaxed text-gray-700 mb-4">
          If you have agreed to receive such marketing, you may always opt out at a later date.
        </p>
        <p className="text-[15px] leading-relaxed text-gray-700">
          You have the right at any time to stop PRAIRI from contacting you for marketing purposes
        </p>
      </section>

      <section>
        <h3 className="text-lg font-extrabold text-gray-900 mb-2 tracking-tight">
          What are your data protection rights?
        </h3>
        <p className="text-[15px] leading-relaxed text-gray-700 mb-4">
          PRAIRI would like to make sure you are fully aware of all of your data protection rights.
        </p>
        <div className="space-y-4 text-[15px] text-gray-700">
          <p>
            <span className="font-bold">The right to access</span> &ndash; You have the right to
            request PRAIRI for copies of your personal data. We may charge you a fee for this
            service.
          </p>
          <p>
            <span className="font-bold">The right to rectification</span> &ndash; You have the right
            to request that PRAIRI correct any information you believe is inaccurate. You also have
            the right to request PRAIRI to complete the information you believe is incomplete.
          </p>
          <p>
            <span className="font-bold">The right to erasure</span> &ndash; You have the right to
            request that PRAIRI erase your personal data, under certain conditions.
          </p>
          <p>
            <span className="font-bold">The right to restrict processing</span> &ndash; You have the
            right to request that PRAIRI restrict the processing of your personal data, under
            certain conditions.
          </p>
          <p>
            <span className="font-bold">The right to object to processing</span> &ndash; You have
            the right to object to PRAIRI&rsquo;s processing of your personal data, under certain
            conditions.
          </p>
          <p>
            <span className="font-bold">The right to data portability</span> &ndash; You have the
            right to request that PRAIRI transfer the data that we have collected to another
            organization, or directly to you, under certain conditions.
          </p>
        </div>
        <p className="text-[15px] leading-relaxed text-gray-700 mt-6">
          If you make a request, we have one month to respond to you. If you would like to exercise
          any of these rights, please contact us at our email:
        </p>
        <p className="text-[15px] font-bold text-gray-900 mt-2">Write to us: contact@prairi.com</p>
      </section>

      <section>
        <h3 className="text-lg font-extrabold text-gray-900 mb-2 tracking-tight">Cookies</h3>
        <p className="text-[15px] leading-relaxed text-gray-700 mb-4">
          Cookies are text files placed on your computer to collect standard Internet log
          information and visitor behavior information. When you visit our websites, we may collect
          information from you automatically through cookies or similar technology
        </p>
        <p className="text-[15px] leading-relaxed text-gray-700">
          For further information, visit{' '}
          <a
            href="https://allaboutcookies.org"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:underline"
          >
            allaboutcookies.org
          </a>
          .
        </p>
      </section>

      <section>
        <h3 className="text-lg font-extrabold text-gray-900 mb-2 tracking-tight">
          How do we use cookies?
        </h3>
        <p className="text-[15px] leading-relaxed text-gray-700 mb-4">
          PRAIRI uses cookies in a range of ways to improve your experience on our website,
          including:
        </p>
        <ul className="list-disc pl-5 space-y-2 text-[15px] text-gray-700">
          <li>Keeping you signed in</li>
          <li>Understanding how you use our website</li>
          <li>Allowing for your easy access to match results</li>
        </ul>
      </section>

      <section>
        <h3 className="text-lg font-extrabold text-gray-900 mb-2 tracking-tight">
          What types of cookies do we use?
        </h3>
        <p className="text-[15px] leading-relaxed text-gray-700 mb-4">
          There are a number of different types of cookies, however, our website uses:
        </p>
        <div className="space-y-4 text-[15px] text-gray-700">
          <p>
            <span className="font-bold">Functionality</span> &ndash; PRAIRI uses these cookies so
            that we recognize you on our website and remember your previously selected preferences.
            These could include what language you prefer and location you are in. A mix of
            first-party and third-party cookies are used.
          </p>
          <p>
            <span className="font-bold">Advertising</span> &ndash; PRAIRI uses these cookies to
            collect information about your visit to our website, the content you viewed, the links
            you followed, and information about your browser, device, and your IP address. PRAIRI
            may, in the future, share some limited aspects of this data with third parties for
            advertising purposes. We may also share online data collected through cookies with our
            advertising partners. This means that when you visit another website, you may be shown
            advertising based on your browsing patterns on our website.
          </p>
        </div>
      </section>

      <section>
        <h3 className="text-lg font-extrabold text-gray-900 mb-2 tracking-tight">
          How to manage cookies
        </h3>
        <p className="text-[15px] leading-relaxed text-gray-700">
          You can set your browser not to accept cookies, and the above website tells you how to
          remove cookies from your browser. However, in a few cases, some of our website features
          may not function as a result.
        </p>
      </section>

      <section>
        <h3 className="text-lg font-extrabold text-gray-900 mb-2 tracking-tight">
          Privacy policies of other websites
        </h3>
        <p className="text-[15px] leading-relaxed text-gray-700">
          The PRAIRI website contains links to other websites. Our privacy policy applies only to
          our website, so if you click on a link to another website, you should read their privacy
          policy.
        </p>
      </section>

      <section>
        <h3 className="text-lg font-extrabold text-gray-900 mb-2 tracking-tight">
          Changes to our privacy policy
        </h3>
        <p className="text-[15px] leading-relaxed text-gray-700">
          PRAIRI keeps its privacy policy under regular review and places any updates on this web
          page. This privacy policy was last updated on 17 November 2023.
        </p>
      </section>

      <section>
        <h3 className="text-lg font-extrabold text-gray-900 mb-2 tracking-tight">
          How to contact us
        </h3>
        <p className="text-[15px] leading-relaxed text-gray-700">
          If you have any questions about PRAIRI&rsquo;s privacy policy, the data we hold on you, or
          you would like to exercise one of your data protection rights, please do not hesitate to
          contact us. Email us at: contact@prairi.com
        </p>
      </section>
    </div>
  );
};

export default PrivacyContent;
